// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type {
  DataBundle,
  Facility,
  Substance,
  Industries,
  SectorSubstance,
  StateYearRow,
  YearTotal,
  TopByYearEntry,
  TransferEntry,
  Insights,
  Meta
} from './types';

const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

export const STATE_LABELS: Record<string, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
  NT: 'Northern Territory'
};

export const STATE_ORDER = STATES;

async function fetchJSON<T>(path: string): Promise<T> {
  const r = await fetch(path);
  if (!r.ok) throw new Error(`Failed to fetch ${path}: HTTP ${r.status}`);
  return r.json() as Promise<T>;
}

export async function loadData(): Promise<DataBundle> {
  const [
    meta,
    facilities,
    substances,
    industries,
    sectorSubstance,
    stateYear,
    yearTotals,
    topByYear,
    transfers,
    insights
  ] = await Promise.all([
    fetchJSON<Meta>('data/meta.json'),
    fetchJSON<Facility[]>('data/facilities.json'),
    fetchJSON<Substance[]>('data/substances.json'),
    fetchJSON<Industries>('data/industries.json'),
    fetchJSON<SectorSubstance[]>('data/sector_substance.json'),
    fetchJSON<StateYearRow[]>('data/state_year.json'),
    fetchJSON<YearTotal[]>('data/year_totals.json'),
    fetchJSON<Record<string, TopByYearEntry[]>>('data/top_by_year.json'),
    fetchJSON<TransferEntry[]>('data/transfers.json'),
    fetchJSON<Insights>('data/insights.json')
  ]);

  return {
    meta,
    facilities,
    substances,
    industries,
    sectorSubstance,
    stateYear,
    yearTotals,
    topByYear,
    transfers,
    insights
  };
}

// Build a lookup map for substance metadata.
export function indexSubstances(substances: Substance[]): Map<number, Substance> {
  return new Map(substances.map(s => [s.id, s]));
}

export function indexFacilities(facilities: Facility[]): Map<number, Facility> {
  return new Map(facilities.map(f => [f.id, f]));
}

export function indexTransfers(transfers: TransferEntry[]): Map<number, TransferEntry> {
  return new Map(transfers.map(t => [t.id, t]));
}
